import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Copy, Mail, MessageCircle, PauseCircle, PlayCircle, CheckCircle2, Clock3, Search, Plus, CalendarDays, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_FORM = {
  title: '',
  amount: '',
  currency: 'UGX',
  due_date: '',
  reminder_type: 'invoice',
  customer_id: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  customer_location: '',
  invoice_id: '',
  payment_link_id: '',
  channels: ['email', 'whatsapp'],
  schedule: {
    before_due_days: 3,
    on_due_date: true,
    after_due_days: 1,
  },
  status: 'active',
};

function fmtMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function statusClass(status, overdue) {
  if (overdue) return 'bg-red-100 text-red-800';
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-emerald-100 text-emerald-800';
  if (s === 'paused') return 'bg-amber-100 text-amber-800';
  if (s === 'completed') return 'bg-slate-100 text-slate-700';
  if (s === 'cancelled') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

function ChannelBadge({ channel }) {
  return (
    <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600 mr-1 mb-1">
      {channel}
    </Badge>
  );
}

export default function PaymentRemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [totals, setTotals] = useState({ total: 0, active: 0, paused: 0, completed: 0, cancelled: 0, overdue: 0 });
  const [access, setAccess] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [links, setLinks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueFilter, setOverdueFilter] = useState('all');
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const canUseReminders = !!access?.can_use_payment_reminders;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateSchedule = (key, value) => setForm((prev) => ({ ...prev, schedule: { ...prev.schedule, [key]: value } }));

  const loadLists = async () => {
    setLoading(true);
    try {
      const [remRes, invRes, linkRes, custRes] = await Promise.allSettled([
        api.get('/dashboard/payment-reminders', {
          params: {
            q: query || undefined,
            status: statusFilter === 'all' ? undefined : statusFilter,
            overdue: overdueFilter === 'all' ? undefined : overdueFilter,
          },
        }),
        api.get('/dashboard/invoices'),
        api.get('/payment-links'),
        api.get('/dashboard/customers'),
      ]);
      if (remRes.status === 'fulfilled') {
        setReminders(remRes.value.data?.reminders || []);
        setTotals(remRes.value.data?.totals || { total: 0, active: 0, paused: 0, completed: 0, cancelled: 0, overdue: 0 });
        setAccess(remRes.value.data?.access || {});
      } else {
        throw remRes.reason;
      }
      setInvoices(invRes.status === 'fulfilled' ? invRes.value.data?.invoices || [] : []);
      setLinks(
        linkRes.status === 'fulfilled'
          ? (Array.isArray(linkRes.value.data) ? linkRes.value.data : linkRes.value.data?.payment_links || linkRes.value.data?.links || [])
          : []
      );
      setCustomers(custRes.status === 'fulfilled' ? custRes.value.data?.customers || [] : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payment reminders');
      setReminders([]);
      setTotals({ total: 0, active: 0, paused: 0, completed: 0, cancelled: 0, overdue: 0 });
      setAccess({});
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      const res = await api.get(`/dashboard/payment-reminders/${id}`);
      setDetail(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reminder');
      setDetail(null);
    }
  };

  useEffect(() => {
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, statusFilter, overdueFilter]);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      setDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const reminderOptions = useMemo(() => reminders.map((item) => ({
    id: item.id,
    label: `${item.title || item.reminder_type} · ${item.customer?.name || 'No customer'}`,
  })), [reminders]);

  const resetForm = () => {
    setSelectedId('');
    setForm(DEFAULT_FORM);
  };

  const beginEdit = (item) => {
    setSelectedId(item.id);
    setForm({
      title: item.title || '',
      amount: item.amount || '',
      currency: item.currency || 'UGX',
      due_date: item.due_date || '',
      reminder_type: item.reminder_type || 'invoice',
      customer_id: item.customer_id || '',
      customer_name: item.customer?.name || '',
      customer_phone: item.customer?.phone || '',
      customer_email: item.customer?.email || '',
      customer_location: item.customer?.location || '',
      invoice_id: item.invoice_id || '',
      payment_link_id: item.payment_link_id || '',
      channels: Array.isArray(item.channels) ? item.channels : ['email', 'whatsapp'],
      schedule: item.schedule || DEFAULT_FORM.schedule,
      status: item.status || 'active',
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canUseReminders) {
      toast.error('Upgrade your package to use payment reminders.');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Reminder title is required.');
      return;
    }
    if (!form.due_date) {
      toast.error('Due date is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount || 0),
        channels: Array.isArray(form.channels) ? form.channels : [],
      };
      const res = selectedId
        ? await api.put(`/dashboard/payment-reminders/${selectedId}`, payload)
        : await api.post('/dashboard/payment-reminders', payload);
      toast.success(selectedId ? 'Reminder updated' : 'Reminder saved');
      const saved = res.data?.reminder || null;
      const nextId = saved?.id || selectedId;
      if (nextId) {
        setSelectedId(nextId);
        await loadDetail(nextId);
      }
      setForm(saved ? {
        title: saved.title || '',
        amount: saved.amount || '',
        currency: saved.currency || 'UGX',
        due_date: saved.due_date || '',
        reminder_type: saved.reminder_type || 'invoice',
        customer_id: saved.customer_id || '',
        customer_name: saved.customer?.name || '',
        customer_phone: saved.customer?.phone || '',
        customer_email: saved.customer?.email || '',
        customer_location: saved.customer?.location || '',
        invoice_id: saved.invoice_id || '',
        payment_link_id: saved.payment_link_id || '',
        channels: Array.isArray(saved.channels) ? saved.channels : ['email', 'whatsapp'],
        schedule: saved.schedule || DEFAULT_FORM.schedule,
        status: saved.status || 'active',
      } : DEFAULT_FORM);
      await loadLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await api.patch(`/dashboard/payment-reminders/${id}/status`, { status });
      toast.success(res.data?.message || 'Reminder status updated');
      await loadLists();
      await loadDetail(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update reminder status');
    }
  };

  const shareWhatsApp = async (id) => {
    try {
      const res = await api.post(`/dashboard/payment-reminders/${id}/share-whatsapp`);
      if (res.data?.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank', 'noopener,noreferrer');
      }
      toast.success('WhatsApp reminder ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate WhatsApp reminder');
    }
  };

  const sendEmail = async (id) => {
    try {
      const res = await api.post(`/dashboard/payment-reminders/${id}/send-email`);
      toast.success(res.data?.message || 'Reminder email sent');
      await loadLists();
      await loadDetail(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder email');
    }
  };

  const filtered = reminders.filter((item) => {
    const haystack = `${item.title || ''} ${item.reminder_type || ''} ${item.customer?.name || ''} ${item.customer?.phone || ''} ${item.customer?.email || ''}`.toLowerCase();
    return !query || haystack.includes(query.toLowerCase());
  });

  const toggleChannel = (channel) => {
    setForm((prev) => {
      const current = Array.isArray(prev.channels) ? prev.channels : [];
      return current.includes(channel)
        ? { ...prev, channels: current.filter((item) => item !== channel) }
        : { ...prev, channels: [...current, channel] };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Reminders</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track invoices, fees, pledges and subscriptions with simple reminders.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={loadLists}>
            <Search size={14} className="mr-1" /> Refresh
          </Button>
          <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={resetForm}>
            <Plus size={14} className="mr-1" /> New Reminder
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {[
          ['Total', totals.total],
          ['Active', totals.active],
          ['Overdue', totals.overdue],
          ['Completed', totals.completed],
          ['Paused', totals.paused],
        ].map(([label, value]) => (
          <Card key={label} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedId ? 'Edit Reminder' : 'Create Reminder'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Title</Label>
              <Input className="mt-1" value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="Invoice reminder, rent reminder..." />
            </div>
            <div>
              <Label>Reminder Type</Label>
              <Select value={form.reminder_type} onValueChange={(v) => update('reminder_type', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['invoice', 'rent', 'school_fees', 'membership', 'pledge', 'installment', 'custom'].map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input className="mt-1" type="number" value={form.amount} onChange={(e) => update('amount', e.target.value)} placeholder="100000" />
            </div>
            <div>
              <Label>Currency</Label>
              <Input className="mt-1" value={form.currency} onChange={(e) => update('currency', e.target.value.toUpperCase())} placeholder="UGX" />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input className="mt-1" type="date" value={form.due_date} onChange={(e) => update('due_date', e.target.value)} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => update('status', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['active', 'paused', 'completed', 'cancelled'].map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 grid gap-3 md:grid-cols-3">
              <div>
                <Label>Customer Name</Label>
                <Input className="mt-1" value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} placeholder="John Doe" />
              </div>
              <div>
                <Label>Customer Phone</Label>
                <Input className="mt-1" value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)} placeholder="2567XXXXXXXX" />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input className="mt-1" type="email" value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} placeholder="customer@example.com" />
              </div>
            </div>
            <div>
              <Label>Customer Location</Label>
              <Input className="mt-1" value={form.customer_location} onChange={(e) => update('customer_location', e.target.value)} placeholder="Kampala" />
            </div>
            <div>
              <Label>Existing Customer</Label>
              <Select value={form.customer_id || 'none'} onValueChange={(v) => update('customer_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>{customer.name}{customer.phone ? ` · ${customer.phone}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice</Label>
              <Select value={form.invoice_id || 'none'} onValueChange={(v) => update('invoice_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {invoices.map((invoice) => (
                    <SelectItem key={invoice.id} value={invoice.id}>{invoice.invoice_number} · {invoice.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Link</Label>
              <Select value={form.payment_link_id || 'none'} onValueChange={(v) => update('payment_link_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {links.map((link) => (
                    <SelectItem key={link.id} value={link.id}>{link.title}{link.slug ? ` · ${link.slug}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Channels</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {['email', 'whatsapp', 'sms'].map((channel) => (
                  <Button
                    key={channel}
                    type="button"
                    variant={form.channels.includes(channel) ? 'default' : 'outline'}
                    className={form.channels.includes(channel) ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'border-slate-200 text-slate-700'}
                    onClick={() => toggleChannel(channel)}
                  >
                    {channel}
                  </Button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 grid gap-3 md:grid-cols-3">
              <div>
                <Label>Before Due (days)</Label>
                <Input className="mt-1" type="number" value={form.schedule.before_due_days} onChange={(e) => updateSchedule('before_due_days', e.target.value)} />
              </div>
              <div>
                <Label>On Due Date</Label>
                <Select value={String(form.schedule.on_due_date)} onValueChange={(v) => updateSchedule('on_due_date', v === 'true')}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>After Due (days)</Label>
                <Input className="mt-1" type="number" value={form.schedule.after_due_days} onChange={(e) => updateSchedule('after_due_days', e.target.value)} />
              </div>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 pt-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                {saving ? 'Saving...' : 'Save Reminder'}
              </Button>
              {selectedId && (
                <Button type="button" variant="outline" onClick={resetForm}>New</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1.45fr,0.95fr]">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Reminders</CardTitle>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <Input placeholder="Search reminders..." value={query} onChange={(e) => setQuery(e.target.value)} />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {['active', 'paused', 'completed', 'cancelled'].map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={overdueFilter} onValueChange={setOverdueFilter}>
                <SelectTrigger><SelectValue placeholder="Overdue" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reminders</SelectItem>
                  <SelectItem value="true">Overdue only</SelectItem>
                  <SelectItem value="false">Not overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="h-32 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reminder</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id} className={selectedId === item.id ? 'bg-emerald-50/40' : ''} onClick={() => setSelectedId(item.id)} role="button">
                      <TableCell>
                        <div className="font-medium text-slate-900">{item.title || item.reminder_type}</div>
                        <div className="text-xs text-slate-500">{item.reminder_type?.replace('_', ' ')}</div>
                        <div className="mt-1 flex flex-wrap">{(item.channels || []).map((channel) => <ChannelBadge key={channel} channel={channel} />)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{item.customer?.name || 'No customer'}</div>
                        <div className="text-xs text-slate-500">{item.customer?.phone || item.customer?.email || '-'}</div>
                      </TableCell>
                      <TableCell>{fmtMoney(item.currency, item.amount)}</TableCell>
                      <TableCell>
                        <div>{fmtDate(item.due_date)}</div>
                        {item.overdue ? <div className="text-xs text-red-600 flex items-center gap-1"><Clock3 size={12} /> {item.days_overdue || 0} day(s) overdue</div> : null}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusClass(item.status, item.overdue)}>
                          {item.overdue ? 'Overdue' : (item.computed_status || item.status || 'active').replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); beginEdit(item); }}>
                            Edit
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); shareWhatsApp(item.id); }}>
                            <MessageCircle size={14} className="mr-1" /> WhatsApp
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); sendEmail(item.id); }}>
                            <Mail size={14} className="mr-1" /> Email
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!loading && !filtered.length && (
              <div className="py-10 text-center text-sm text-slate-500">No reminders found.</div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Reminder Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {detail ? (
              <>
                <div className="space-y-1">
                  <div className="text-sm text-slate-500">Title</div>
                  <div className="font-medium text-slate-900">{detail.title}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-xs text-slate-500">Customer</div>
                    <div className="font-medium text-slate-900">{detail.customer?.name || '-'}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-xs text-slate-500">Due</div>
                    <div className="font-medium text-slate-900">{fmtDate(detail.due_date)}</div>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500">Message</div>
                  <p className="text-sm text-slate-700 mt-2 leading-6">{detail.message}</p>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500">WhatsApp Link</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(detail.whatsapp_url || '');
                        toast.success('WhatsApp link copied');
                      }}
                    >
                      <Copy size={14} className="mr-1" /> Copy
                    </Button>
                    <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.open(detail.whatsapp_url, '_blank', 'noopener,noreferrer')}>
                      <MessageCircle size={14} className="mr-1" /> Open WhatsApp
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => setStatus(detail.id, 'active')}><PlayCircle size={14} className="mr-1" /> Active</Button>
                  <Button type="button" variant="outline" onClick={() => setStatus(detail.id, 'paused')}><PauseCircle size={14} className="mr-1" /> Pause</Button>
                  <Button type="button" variant="outline" onClick={() => setStatus(detail.id, 'completed')}><CheckCircle2 size={14} className="mr-1" /> Complete</Button>
                </div>
                <div className="rounded-xl bg-white border border-slate-100 p-3">
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Link2 size={14} /> Linked documents</div>
                  <div className="mt-2 text-sm text-slate-700 space-y-1">
                    <div>Invoice: {detail.invoice?.invoice_number || detail.invoice_id || '-'}</div>
                    <div>Payment link: {detail.payment_link?.title || detail.payment_link_id || '-'}</div>
                    <div>Schedule: before {detail.schedule?.before_due_days || 0}, on due date {detail.schedule?.on_due_date ? 'yes' : 'no'}, after {detail.schedule?.after_due_days || 0}</div>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500 mb-2">Activity</div>
                  <div className="space-y-2">
                    {(detail.events || []).length ? detail.events.map((event) => (
                      <div key={event.id} className="text-sm text-slate-700">
                        <span className="font-medium">{event.event_type}</span> · {event.message || '-'}
                        <div className="text-xs text-slate-500">{fmtDate(event.created_at)}</div>
                      </div>
                    )) : <div className="text-sm text-slate-500">No reminder activity yet.</div>}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-500">
                Select a reminder to view its message, WhatsApp link and activity.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
